import * as React from 'react';
import CalendarItem from 'owa-service/lib/contract/CalendarItem';
import { AriaRoles } from 'owa-accessibility';
import loc, { format } from 'owa-localize';
import {
    otherEventsCountLabel,
    conflictingMeetingAriaLabel,
} from './ConflictingMeetingList.locstring.json';
import formatConflictingTimeSpan from 'owa-timeformat/lib/utils/formatConflictingTimeSpan';
import popoutCalendarReadingPane from 'owa-popout-utils/lib/utils/popoutCalendarReadingPane';
import { AddPopoutSource } from 'owa-popout-v2';

const styles = require('./MeetingRequestConflicts.scss');

const MAX_CONFLICT_TO_SHOW = 3;

interface ConflictingMeetingListProps {
    items: CalendarItem[];
    headerText: string;
    isMeetingRequestRecurringMaster: boolean;
}

export default function ConflictingMeetingList(props: ConflictingMeetingListProps): JSX.Element {
    const { items, headerText, isMeetingRequestRecurringMaster } = props;

    const conflictCalendarItemsToShow = items.slice(0, MAX_CONFLICT_TO_SHOW);
    return (
        <div className={styles.conflictListGroup} aria-label={headerText} role={AriaRoles.group}>
            <span className={styles.listHeader}>{headerText}</span>
            {conflictCalendarItemsToShow.map(item => (
                <ConflictMeetingEntry
                    key={item.ItemId.Id}
                    itemId={item.ItemId.Id}
                    subject={item.Subject}
                    start={item.Start}
                    end={item.End}
                    isMeetingRequestRecurringMaster={isMeetingRequestRecurringMaster}
                />
            ))}
            {items.length > MAX_CONFLICT_TO_SHOW && (
                <span className={styles.otherEventsCount}>
                    {format(loc(otherEventsCountLabel), items.length - MAX_CONFLICT_TO_SHOW)}
                </span>
            )}
        </div>
    );
}

interface ConflictMeetingEntryProps {
    itemId: string;
    subject: string;
    start: string;
    end: string;
    isMeetingRequestRecurringMaster: boolean;
}

function ConflictMeetingEntry(props: ConflictMeetingEntryProps): JSX.Element {
    const { itemId, subject, start, end, isMeetingRequestRecurringMaster } = props;

    const meetingTimeToDisplay = formatConflictingTimeSpan(
        start,
        end,
        isMeetingRequestRecurringMaster /* showDate */
    );
    const ariaLabel = format(loc(conflictingMeetingAriaLabel), subject, meetingTimeToDisplay);
    const onMeetingClicked = React.useCallback(
        (evt: React.MouseEvent<unknown>) => {
            // Stop the click event for the outer container.
            evt.stopPropagation();

            popoutCalendarReadingPane(
                itemId,
                'Mail_MeetingRequestConflict',
                null /* data */,
                AddPopoutSource.MailMeetingConflicts
            );
        },
        [itemId]
    );

    return (
        <div key={itemId} className={styles.meetingListItemContainer}>
            {/* TODO: VSO:10962 - calendar bar should be based on the color of the calendar */}
            <div
                className={styles.meetingItem}
                tabIndex={0}
                aria-label={ariaLabel}
                role={AriaRoles.button}
                onClick={onMeetingClicked}>
                <div className={styles.firstColumn} />
                <div className={styles.secondColumn}>
                    <div className={styles.meetingSubject} title={subject}>
                        {subject}
                    </div>
                    <div className={styles.meetingTime} title={meetingTimeToDisplay}>
                        {meetingTimeToDisplay}
                    </div>
                </div>
            </div>
        </div>
    );
}
